import { Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import * as React from 'react';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { ShippingMethods } from '@/enums/shippingMethod';
import { longFormatDate } from '@/utils/dates';
import { getDisputeHelpUrl } from '@/utils/urls';

export const paymentStatusPrimaryMessages = {
    assumePaid: <FormattedMessage id="wonitems.badges.paid" />,
    delivered: <FormattedMessage id="wonitems.badges.delivered" />,
    due: (
        <>
            <Warning
                className="mr-[5px] align-sub"
                color="critical"
                size="sm"
                style="fill"
            />
            <FormattedMessage id="wonitems.badges.payment_due" />
        </>
    ),
    inTransit: <FormattedMessage id="wonitems.badges.in_transit" />,
    invoicePending: <FormattedMessage id="wonitems.invoice_pending" />,
    orderComplete: <FormattedMessage id="wonitems.badges.orderComplete" />,
    overdue: (
        <>
            <Warning
                className="mr-[5px] align-sub"
                color="critical"
                size="sm"
                style="fill"
            />
            <FormattedMessage id="wonitems.badges.dispute" />
        </>
    ),
    paid: <FormattedMessage id="wonitems.badges.paid" />,
    readyToShip: <FormattedMessage id="wonitems.badges.ready_to_ship" />,
    resolved: <FormattedMessage id="wonitems.badges.dispute_resolved" />,
    shippingStatusUnavailable: <FormattedMessage id="wonitems.badges.shipping_status_unavailable" />,
    [ShippingMethods.BidderShipping]: <FormattedMessage id="wonitems.badges.self_arranged_shipping" />,
    [ShippingMethods.Pickup]: <FormattedMessage id="wonitems.badges.pickup" />,
};

export const paymentStatusSecondaryMessages = {
    addShippingEligible: (amount: number, currency: string, deadline: Date) => (
        <FormattedMessage
            id="wonitems.badges.addShippingEligible"
            values={{
                deadline: longFormatDate(deadline.getTime()),
                quote: (
                    <FormattedCurrency
                        key="won-items-add-shipping-eligible-quote"
                        sourceCurrency={currency}
                        value={amount}
                    />
                ),
            }}
        />
    ),
    addShippingIneligible: (openShippingPolicyModal: () => void) => (
        <FormattedMessage
            id="wonitems.badges.addShippingIneligible"
            values={{
                policyLink: (
                    <Link
                        key="won-items-add-shipping-ineligible-policy-link"
                        onClick={openShippingPolicyModal}
                    >
                        <FormattedMessage id="wonitems.badges.shippingPolicy" />.
                    </Link>
                ),
            }}
        />
    ),
    assumePaid: <FormattedMessage id="wonitems.badges.assume_paid_submessage" />,
    delivered: <FormattedMessage id="wonitems.badges.delivered_submessage" />,
    due: <FormattedMessage id="wonitems.badges.payment_due_submessage" />,
    inTransit: <FormattedMessage id="wonitems.badges.in_transit_submessage" />,
    invoicePending: <FormattedMessage id="wonitems.badges.invoice_pending_submessage" />,
    orderComplete: <FormattedMessage id="wonitems.badges.orderCompleteSubmessage" />,
    overdue: (
        <>
            <FormattedMessage id="wonitems.badges.dispute_submessage" />{' '}
            <Link
                target="_blank"
                href={getDisputeHelpUrl()}
            >
                <FormattedMessage id="review.learnMoreHere" />.
            </Link>
        </>
    ),
    paid: <FormattedMessage id="wonitems.badges.paid_submessage" />,
    paymentPending: <FormattedMessage id="wonitems.shippingStatus.secondary.youWillSeeStatus" />,
    readyToShip: <FormattedMessage id="wonitems.badges.ready_to_ship_submessage" />,
    resolved: <FormattedMessage id="wonitems.badges.dispute_resolved_submessage" />,
    shippingStatusUnavailable: <FormattedMessage id="wonitems.badges.shipping_status_unavailable_submessage" />,
    [ShippingMethods.BidderShipping]: <FormattedMessage id="wonitems.badges.self_arranged_shipping_submessage" />,
    [ShippingMethods.Pickup]: (houseAddress: string) => (
        <FormattedMessage
            id="wonitems.badges.pickup_submessage"
            values={{
                address: (
                    <Link
                        key="won-items-pickup-submessage-address-link"
                        target="_blank"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(houseAddress)}`}
                    >
                        {houseAddress}
                    </Link>
                ),
            }}
        />
    ),
};

// the api comes back with an English string
// for translation purposes, we're mapping it to FormattedMessages
export const shippingStatusMessagesPrimary = {
    'Awaiting Pickup': <FormattedMessage id="wonitems.shippingStatus.primary.awaitingPickup" />,
    Delivered: <FormattedMessage id="wonitems.shippingStatus.primary.delivered" />,
    'In Transit': <FormattedMessage id="wonitems.shippingStatus.primary.inTransit" />,
    'Invoice Paid': <FormattedMessage id="wonitems.shippingStatus.primary.invoicePaid" />,
    'Order Completed': <FormattedMessage id="wonitems.shippingStatus.primary.orderCompleted" />,
    'Order Created': <FormattedMessage id="wonitems.shippingStatus.primary.orderCreated" />,
    'Out for Delivery': <FormattedMessage id="wonitems.shippingStatus.primary.outForDelivery" />,
    Packaging: <FormattedMessage id="wonitems.shippingStatus.primary.packaging" />,
    'Waiting for Payment': <FormattedMessage id="wonitems.shippingStatus.primary.waitingForPayment" />,
};

// the api comes back with an English string
// for translation purposes, we're mapping it to FormattedMessages
export const shippingStatusMessagesSecondary = {
    'Our trusted shipping partner received your order': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.trustedPartnerReceived" />
    ),
    'Please review the auction house shipping policy and arrange shipping': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.arrangeShipping" />
    ),
    'Thank you for your order': <FormattedMessage id="wonitems.shippingStatus.secondary.thanks" />,
    'The carrier could not deliver your order and will reach out to reschedule it': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.carrierDeliveryFailure" />
    ),
    'This invoice has been marked as paid because it is over 50 days old without being disputed': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.markedAsPaid" />
    ),
    'You will see the shipping status once the payment is processed': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.youWillSeeStatus" />
    ),
    'Your invoice has been sent with payment instructions. Be sure to check your email inbox and junk folder. Contact auction house for any question.':
        <FormattedMessage id="wonitems.shippingStatus.secondary.paymentInstructions" />,
    'Your package has been delivered': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.directDeliverySuccess" />
    ),
    'Your package has been delivered to the pick up location': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.pickupDeliverySuccess" />
    ),
    'Your package has been packed': <FormattedMessage id="wonitems.shippingStatus.secondary.packed" />,
    'Your package has been picked up from the auction house': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.collectedFromHouse" />
    ),
    'Your package has been returned to the shipper': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.returnToSender" />
    ),
    'Your package has been scheduled to be picked up': (
        <FormattedMessage id="wonitems.shippingStatus.secondary.scheduledPickup" />
    ),
    'Your package is on its way to you': <FormattedMessage id="wonitems.shippingStatus.secondary.inTransit" />,
};
